import { useEffect, useState } from 'react';
import { Me, Public } from '../api.js';
import { FormField, Switch } from '../components/FormField.jsx';
import { DynamicField, validateCustomFieldsClient } from '../components/DynamicField.jsx';
import { PageHeader } from '../components/PageHeader.jsx';
import { ChangePasswordModal } from '../components/ChangePassword.jsx';
import { MfaEnrollModal } from '../components/MfaEnrollModal.jsx';
import { useAuth } from '../AuthContext.jsx';
import { auth, sendEmailVerification, authActionSettings, multiFactor } from '../firebase.js';

export default function Profile() {
  const { emailVerified, refreshVerification } = useAuth();
  const [user, setUser] = useState(null);
  const [customDefs, setCustomDefs] = useState([]);
  const [customFields, setCustomFields] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [verifyState, setVerifyState] = useState('idle');
  const [verifyErr, setVerifyErr] = useState(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState(null);
  const [mfaModalOpen, setMfaModalOpen] = useState(false);
  const [mfaFactors, setMfaFactors] = useState([]);
  const [mfaBusy, setMfaBusy] = useState(false);

  function refreshMfa() {
    const u = auth.currentUser;
    if (!u) { setMfaFactors([]); return; }
    try { setMfaFactors(multiFactor(u).enrolledFactors || []); }
    catch { setMfaFactors([]); }
  }
  useEffect(() => { refreshMfa(); }, [user]);

  async function removeMfa(factor) {
    if (!confirm(`Remove ${factor.displayName || 'this factor'}? You'll sign in with just your password until you add a new factor.`)) return;
    setMfaBusy(true);
    try {
      await multiFactor(auth.currentUser).unenroll(factor);
      refreshMfa();
    } catch (e) {
      if (e?.code === 'auth/requires-recent-login') {
        alert('Session too old. Sign out and sign back in, then try again.');
      } else {
        alert(e.message || 'Could not remove factor.');
      }
    } finally {
      setMfaBusy(false);
    }
  }

  useEffect(() => {
    Me.get().then((r) => {
      setUser(r.user);
      setCustomFields(r.user.customFields || {});
    });
    Public.signupFields().then((r) => setCustomDefs(r.fields)).catch(() => setCustomDefs([]));
  }, []);

  function updateCustom(key, v) {
    setCustomFields((c) => ({ ...c, [key]: v }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  async function resendVerification() {
    if (!auth.currentUser) return;
    setVerifyState('sending');
    setVerifyErr(null);
    try {
      await sendEmailVerification(auth.currentUser, authActionSettings);
      setVerifyState('sent');
    } catch (e) {
      setVerifyState('error');
      setVerifyErr(e.message);
    }
  }

  async function checkVerification() {
    const ok = await refreshVerification();
    if (!ok) setMsg('Still not verified — check your inbox and click the link.');
    else setMsg('Verified!');
  }

  async function save(e) {
    e.preventDefault();
    setMsg(null);
    setFieldErrors({});

    const pre = validateCustomFieldsClient(customDefs, customFields);
    if (!pre.ok) {
      setFieldErrors(pre.errorsByKey);
      setMsg('Please fix the highlighted fields.');
      return;
    }

    setSaving(true);
    try {
      const r = await Me.update({
        displayName: user.displayName,
        phone: user.phone,
        marketingOptIn: user.marketingOptIn,
        customFields,
      });
      setUser(r.user);
      setCustomFields(r.user.customFields || {});
      setMsg('Saved.');
    } catch (e) {
      if (e.data?.issues && Array.isArray(e.data.issues)) {
        const byKey = {};
        for (const it of e.data.issues) if (it.key) byKey[it.key] = it.message;
        setFieldErrors(byKey);
        setMsg('Please fix the highlighted fields.');
      } else {
        setMsg(e.message);
      }
    } finally {
      setSaving(false);
    }
  }

  if (!user) return <div className="p-8 text-ink-3">Loading…</div>;

  return (
    <div className="max-w-xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <PageHeader eyebrow="Account" title="Your profile" />
      {!emailVerified && (
        <div className="card mb-6 bg-warn/5 border-warn/30">
          <div className="flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="eyebrow text-warn mb-1">Email not verified</div>
              <p className="text-sm text-ink">
                We sent a verification link to <strong>{user.email}</strong>. Click it to confirm your
                address. Already clicked? Hit <em>I've verified</em> to refresh.
              </p>
              {verifyState === 'sent' && (
                <p className="text-xs text-success mt-2">Verification email sent. Check your inbox.</p>
              )}
              {verifyErr && <p className="text-xs text-danger mt-2">{verifyErr}</p>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              type="button"
              onClick={resendVerification}
              className="btn-outline btn-sm"
              disabled={verifyState === 'sending'}
            >
              {verifyState === 'sending' ? 'Sending…' : 'Resend verification'}
            </button>
            <button type="button" onClick={checkVerification} className="btn-ghost btn-sm">
              I've verified — refresh
            </button>
          </div>
        </div>
      )}
      <form onSubmit={save} className="card space-y-5">
        <FormField label="Email">
          <input className="input bg-line-soft/50 text-ink-2" value={user.email} disabled />
        </FormField>
        <FormField label="Name">
          <input
            className="input"
            value={user.displayName || ''}
            onChange={(e) => setUser({ ...user, displayName: e.target.value })}
          />
        </FormField>
        <FormField label="Phone">
          <input
            className="input"
            value={user.phone || ''}
            onChange={(e) => setUser({ ...user, phone: e.target.value })}
          />
        </FormField>

        {customDefs.length > 0 && (
          <div className="pt-2 border-t border-line space-y-5">
            <div className="eyebrow">About you</div>
            {customDefs.map((f) => (
              <DynamicField
                key={f.id}
                field={f}
                value={customFields[f.key]}
                onChange={(v) => updateCustom(f.key, v)}
                error={fieldErrors[f.key]}
              />
            ))}
          </div>
        )}

        <div className="pt-2 border-t border-line">
          <Switch
            checked={!!user.marketingOptIn}
            onChange={(v) => setUser({ ...user, marketingOptIn: v })}
            label="Receive marketing emails"
            hint="Club news, fixtures, and fundraisers"
          />
        </div>
        <div className="flex items-center gap-3 pt-2">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          {msg && <span className="text-xs text-ink-2">{msg}</span>}
        </div>
      </form>

      <div className="mt-10 pt-6 border-t border-line">
        <div className="eyebrow mb-2">Security</div>
        <div className="space-y-5">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-medium">Password</div>
              <div className="text-xs text-ink-3">
                {passwordMsg || 'Update the password you use to sign in.'}
              </div>
            </div>
            <button className="btn-outline btn-sm shrink-0" onClick={() => { setPasswordMsg(null); setPasswordModalOpen(true); }}>
              Change password
            </button>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-medium">Two-factor authentication</div>
              {mfaFactors.length === 0 ? (
                <div className="text-xs text-ink-3">Not enabled. Add an SMS code as a second step at sign-in.</div>
              ) : (
                <ul className="text-xs text-ink-2 space-y-1 mt-1">
                  {mfaFactors.map((f) => (
                    <li key={f.uid} className="flex items-center gap-2">
                      <span>📱 {f.phoneNumber || f.displayName || 'Phone factor'}</span>
                      <button
                        className="text-danger underline underline-offset-2"
                        onClick={() => removeMfa(f)}
                        disabled={mfaBusy}
                      >
                        remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {mfaFactors.length === 0 && (
              <button className="btn-outline btn-sm shrink-0" onClick={() => setMfaModalOpen(true)}>
                Enable SMS two-factor
              </button>
            )}
          </div>
        </div>
      </div>

      <ChangePasswordModal
        open={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        onSuccess={() => setPasswordMsg('Password updated.')}
      />
      <MfaEnrollModal
        open={mfaModalOpen}
        onClose={() => setMfaModalOpen(false)}
        onSuccess={refreshMfa}
      />
    </div>
  );
}
